import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BRAND, NAV } from "../lib/site-data";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const primaryPhone = BRAND.phones[0];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || pathname !== "/"
          ? "bg-[#1f2935]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 relative flex items-center justify-center">
        {/* Left — Call CTA */}
        <a
          href={`tel:${primaryPhone.tel}`}
          className="absolute left-4 sm:left-6 lg:left-10 inline-flex items-center gap-2 text-[11px] sm:text-[12px] uppercase tracking-[0.18em] text-slate-200 hover:text-[#D4AF37] transition-colors group"
          data-testid="nav-call-link"
          aria-label={`Call ${primaryPhone.value}`}
        >
          <span className="h-8 w-8 rounded-full border border-white/15 bg-white/5 flex items-center justify-center group-hover:border-[#D4AF37]/60 transition-colors">
            <Phone size={13} strokeWidth={1.75} className="text-[#D4AF37]" />
          </span>
          <span className="hidden sm:inline font-medium">
            {primaryPhone.value}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.path}
              to={n.path}
              end={n.path === "/"}
              className={({ isActive }) =>
                `dn-link-underline text-[12px] uppercase tracking-[0.18em] transition-colors ${
                  isActive
                    ? "text-[#D4AF37]"
                    : "text-slate-300 hover:text-white"
                }`
              }
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center ml-8">
          <Link
            to="/quote"
            className="px-4 py-2 rounded-full bg-[#D4AF37] text-[#1f2935] font-semibold text-[11px] uppercase tracking-[0.16em] hover:bg-[#B48600] transition-colors"
            data-testid="nav-quote-btn"
          >
            Get a Quote
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className="lg:hidden absolute right-4 sm:right-6 p-2 text-white"
              aria-label="Open menu"
              data-testid="mobile-menu-trigger"
            >
              <Menu size={24} strokeWidth={1.5} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="bg-[#1f2935] border-l border-white/10 w-[78vw] max-w-[320px] p-0"
          >
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <SheetDescription className="sr-only">
              Site navigation
            </SheetDescription>
            <div className="h-14 border-b border-white/10" />
            <nav className="flex flex-col px-2 py-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.path}
                  to={n.path}
                  end={n.path === "/"}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-md text-base transition-colors ${
                      isActive
                        ? "text-[#D4AF37] bg-white/5"
                        : "text-white hover:bg-white/5"
                    }`
                  }
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="px-4 mt-2 space-y-3">
              <Link
                to="/quote"
                className="block w-full text-center py-2.5 rounded-full bg-[#D4AF37] text-[#1f2935] font-semibold text-[12px] uppercase tracking-[0.16em]"
                data-testid="mobile-nav-quote-btn"
              >
                Get a Quote
              </Link>
              <div className="pt-3 border-t border-white/5 space-y-2 text-[13px]">
                {BRAND.phones.map((p) => (
                  <a
                    key={p.tel}
                    href={`tel:${p.tel}`}
                    className="block px-2 py-1.5 text-slate-300 hover:text-[#D4AF37] transition-colors"
                    data-testid={`mobile-nav-call-${p.name.toLowerCase()}`}
                  >
                    <span className="text-slate-500 mr-2">{p.name}</span>
                    {p.value}
                  </a>
                ))}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
